import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  AcademicCapIcon, 
  BookOpenIcon, 
  SparklesIcon,
  PlayIcon,
  ArrowRightIcon,
  StarIcon,
  UsersIcon,
  GlobeAltIcon,
  FireIcon,
  TrophyIcon,
  LightBulbIcon,
  HeartIcon
} from '@heroicons/react/24/outline';
import './styles/Homepage.css';

const Homepage = () => {
  const canvasRef = useRef(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let particles = [];
    let animationId;

    // Resize canvas
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    // Advanced Particle class with Japanese characters
    class Particle {
      constructor() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.vx = (Math.random() - 0.5) * 1.5;
        this.vy = (Math.random() - 0.5) * 1.5;
        this.size = Math.random() * 4 + 2;
        this.opacity = Math.random() * 0.6 + 0.3;
        this.rotation = Math.random() * Math.PI * 2;
        this.rotationSpeed = (Math.random() - 0.5) * 0.02;
        this.characters = ['日', '本', '語', '学', '習', '勉', '強', '読', '書', '話', '聞', '見', '思', '考', '理', '解'];
        this.character = this.characters[Math.floor(Math.random() * this.characters.length)];
        this.color = `hsl(${Math.random() * 60 + 320}, 80%, 70%)`;
        this.pulse = Math.random() * Math.PI * 2;
      }

      update() {
        this.x += this.vx;
        this.y += this.vy;
        this.rotation += this.rotationSpeed;
        this.pulse += 0.05;

        if (this.x < 0 || this.x > canvas.width) this.vx *= -1;
        if (this.y < 0 || this.y > canvas.height) this.vy *= -1;

        // Mouse interaction
        const dx = mousePosition.x - this.x;
        const dy = mousePosition.y - this.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        if (distance < 150) {
          const force = (150 - distance) / 150;
          this.x -= dx * force * 0.02;
          this.y -= dy * force * 0.02;
        }
      }

      draw() {
        ctx.save();
        ctx.globalAlpha = this.opacity * (0.8 + 0.2 * Math.sin(this.pulse));
        ctx.fillStyle = this.color;
        ctx.translate(this.x, this.y);
        ctx.rotate(this.rotation);
        ctx.font = `${this.size * 3}px serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(this.character, 0, 0);
        ctx.restore();
      }
    }

    // Initialize particles
    const initParticles = () => {
      particles = [];
      for (let i = 0; i < 30; i++) {
        particles.push(new Particle());
      }
    };

    // Animation loop
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      particles.forEach(particle => {
        particle.update();
        particle.draw();
      });

      // Draw connections with gradient
      particles.forEach((particle, i) => {
        particles.slice(i + 1).forEach(otherParticle => {
          const dx = particle.x - otherParticle.x;
          const dy = particle.y - otherParticle.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          
          if (distance < 120) {
            ctx.save();
            const alpha = (120 - distance) / 120 * 0.3;
            const gradient = ctx.createLinearGradient(particle.x, particle.y, otherParticle.x, otherParticle.y);
            gradient.addColorStop(0, `rgba(220, 38, 38, ${alpha})`);
            gradient.addColorStop(1, `rgba(239, 68, 68, ${alpha})`);
            ctx.strokeStyle = gradient;
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.moveTo(particle.x, particle.y);
            ctx.lineTo(otherParticle.x, otherParticle.y);
            ctx.stroke();
            ctx.restore();
          }
        });
      });

      animationId = requestAnimationFrame(animate);
    };

    // Mouse move handler
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };

    // Initialize
    resizeCanvas();
    initParticles();
    animate();
    setIsLoaded(true);

    // Event listeners
    window.addEventListener('resize', resizeCanvas);
    window.addEventListener('mousemove', handleMouseMove);

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      window.removeEventListener('mousemove', handleMouseMove);
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, [mousePosition]);

  return (
    <div className="homepage">
      {/* Background Canvas */}
      <canvas 
        ref={canvasRef} 
        className="homepage-canvas"
        style={{ position: 'fixed', top: 0, left: 0, zIndex: -1 }}
      />
      
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <div className="hero-badge">
            <FireIcon className="hero-badge-icon" />
            <span>Học tiếng Nhật với AI thông minh</span>
          </div>
          
          <h1 className="hero-title">
            <span className="hero-title-main">Kaizen</span>
            <span className="hero-title-sub">Japanese</span>
            <span className="hero-title-accent">改善</span>
          </h1>
          
          <p className="hero-description">
            Khám phá vẻ đẹp của tiếng Nhật với công nghệ AI tiên tiến. 
            Học từ vựng, luyện phát âm và đắm mình trong ngôn ngữ tuyệt vời của xứ sở mặt trời mọc.
          </p>
          
          <div className="hero-buttons">
            <Link to="/search-word" className="hero-btn primary">
              <PlayIcon className="hero-btn-icon" />
              Bắt đầu học ngay
              <ArrowRightIcon className="hero-btn-arrow" />
            </Link>
            <Link to="/vocabulary-review" className="hero-btn secondary">
              <TrophyIcon className="hero-btn-icon" />
              Ôn tập từ vựng
            </Link>
          </div>
        </div>
        
        <div className="hero-visual">
          {/* Central Japanese Text */}
          <div className="central-text">
            <div className="japanese-text">日本語</div>
            <div className="japanese-subtitle">Nihongo</div>
            <div className="central-glow"></div>
          </div>
          
          {/* Orbiting Planets */}
          <div className="orbit-container">
            <div className="planet planet-1">
              <div className="planet-core">
                <BookOpenIcon className="planet-icon" />
              </div>
              <div className="planet-ring"></div>
              <div className="planet-label">Từ vựng</div>
            </div>
            
            <div className="planet planet-2">
              <div className="planet-core">
                <GlobeAltIcon className="planet-icon" />
              </div>
              <div className="planet-ring"></div>
              <div className="planet-label">Dịch thuật</div>
            </div>
            
            <div className="planet planet-3">
              <div className="planet-core">
                <HeartIcon className="planet-icon" />
              </div>
              <div className="planet-ring"></div>
              <div className="planet-label">Học tập</div>
            </div>
            
            <div className="planet planet-4">
              <div className="planet-core">
                <TrophyIcon className="planet-icon" />
              </div>
              <div className="planet-ring"></div>
              <div className="planet-label">Thành tích</div>
            </div>
            
            <div className="planet planet-5">
              <div className="planet-core">
                <LightBulbIcon className="planet-icon" />
              </div>
              <div className="planet-ring"></div>
              <div className="planet-label">AI Thông minh</div>
            </div>
            
            <div className="planet planet-6">
              <div className="planet-core">
                <SparklesIcon className="planet-icon" />
              </div>
              <div className="planet-ring"></div>
              <div className="planet-label">Ma thuật</div>
            </div>
          </div>
          
          {/* Stars */}
          <div className="star star-1"></div>
          <div className="star star-2"></div>
          <div className="star star-3"></div>
          <div className="star star-4"></div>
          <div className="star star-5"></div>
          <div className="star star-6"></div>
          <div className="star star-7"></div>
          <div className="star star-8"></div>
          <div className="star star-9"></div>
          <div className="star star-10"></div>
          
          {/* Comet */}
          <div className="comet">
            <div className="comet-tail"></div>
            <div className="comet-head"></div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <div className="container">
          <h2 className="features-title">Tại sao chọn Kaizen Japanese?</h2>
          
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">
                <LightBulbIcon />
              </div>
              <h3>AI Thông Minh</h3>
              <p>Thuật toán AI tiên tiến thích ứng với phong cách học tập của bạn, cung cấp bài học cá nhân hóa.</p>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon">
                <BookOpenIcon />
              </div>
              <h3>Từ vựng phong phú</h3>
              <p>Truy cập hàng nghìn từ tiếng Nhật với ý nghĩa chi tiết, ví dụ và ngữ cảnh sử dụng.</p>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon">
                <GlobeAltIcon />
              </div>
              <h3>Dịch thuật thông minh</h3>
              <p>Dịch văn bản và tài liệu với AI hiểu biết sâu sắc về sắc thái tiếng Nhật.</p>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon">
                <TrophyIcon />
              </div>
              <h3>Ôn tập tương tác</h3>
              <p>Tham gia các phiên ôn tập gamified khiến việc học trở nên thú vị và dễ nhớ.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section">
        <div className="container">
          <div className="stats-grid">
            <div className="stat-item">
              <div className="stat-number">10,000+</div>
              <div className="stat-label">Từ vựng</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">5,000+</div>
              <div className="stat-label">Học viên</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">98%</div>
              <div className="stat-label">Tỷ lệ thành công</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">24/7</div>
              <div className="stat-label">Hỗ trợ AI</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Sẵn sàng bắt đầu hành trình tiếng Nhật?</h2>
            <p>Tham gia cùng hàng nghìn học viên đang thành thạo tiếng Nhật với Kaizen.</p>
            <Link to="/search-word" className="cta-button">
              <PlayIcon className="cta-icon" />
              Bắt đầu ngay
              <ArrowRightIcon className="cta-arrow" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Homepage;
